import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import LoadingSection from "../LoadingSection";
import { selectSupplierOrderDetails, selectSupplierOrderDetailsLoading, deleteSupplierOrder, selectSupplierOrderDeleteLoading, selectSupplierOrderDeleteError } from "../../redux/supplierOrders/supplierOrdersSlice";
import { closeModal, setHasToRefresh, setModal } from "../../redux/rightModalSlice";
import moment from "moment";

const statusColors = {
    'bozza': 'bg-gray-100 text-gray-700',
    'confermato': 'bg-blue-100 text-blue-700',
    'in_consegna': 'bg-yellow-100 text-yellow-700',
    'consegnato': 'bg-green-100 text-green-700',
};

export default function ViewSupplierOrder() {
    const dispatch = useDispatch();
    const orderDetails = useSelector(selectSupplierOrderDetails);
    const orderDetailsLoading = useSelector(selectSupplierOrderDetailsLoading);
    const deleteLoading = useSelector(selectSupplierOrderDeleteLoading);
    const deleteError = useSelector(selectSupplierOrderDeleteError);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');

    const handleEditClick = () => {
        dispatch(setModal({ mode: 'edit', id: orderDetails._id }));
    };

    const confirmDelete = () => {
        if (orderDetails?._id) {
            dispatch(deleteSupplierOrder({ supplierOrderId: orderDetails._id, token }))
                .then((result) => {
                    if (!result.error) {
                        dispatch(closeModal());
                        dispatch(setHasToRefresh());
                    }
                });
        }
    };

    if (orderDetailsLoading) {
        return <LoadingSection />;
    }

    if (!orderDetails) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Ordine non trovato</h2>
                <p className="text-gray-600">L'ordine richiesto non è disponibile.</p>
                <div className="mt-4">
                    <button className="secondary" onClick={() => dispatch(closeModal())}>
                        <DynamicIcon iconClass="fas fa-arrow-left" />
                        Torna indietro
                    </button>
                </div>
            </div>
        );
    }

    const statusClass = statusColors[orderDetails.statusLabel?.toLowerCase()] || 'bg-gray-100 text-gray-700';

    // Calcolo totale ordine
    const orderTotal = orderDetails.items?.reduce((total, item) => {
        const price = item.purchasePriceDiscounted || item.purchasePrice || 0;
        return total + (price * (item.quantityTotal || 1));
    }, 0) || 0;

    return (
        <div className="p-6">
            {deleteLoading && <LoadingSection isFixed />}

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Dettaglio Ordine {orderDetails.code}</h2>
                <div className="flex space-x-2">
                    <button className="secondary" onClick={handleEditClick}>
                        <DynamicIcon iconClass="fas fa-edit" />
                        Modifica
                    </button>
                    <button className="danger" onClick={() => setShowDeleteConfirm(true)}>
                        <DynamicIcon iconClass="fas fa-trash" />
                        Elimina
                    </button>
                </div>
            </div>

            {/* Dati Ordine */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Dati Ordine</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Codice</h4>
                        <p className="text-base font-semibold">{orderDetails.code || '-'}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Fornitore</h4>
                        <p className="text-base font-semibold">{orderDetails.supplierLabel || '-'}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Stato</h4>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusClass}`}>
                            {orderDetails.statusLabel || 'Bozza'}
                        </span>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Data Ordine</h4>
                        <p className="text-base">{orderDetails.supplierOrderDate ? moment(orderDetails.supplierOrderDate).format('DD/MM/YYYY') : '-'}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Data Consegna</h4>
                        <p className="text-base">{orderDetails.deliveryDate ? moment(orderDetails.deliveryDate).format('DD/MM/YYYY') : '-'}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Settimana Consegna</h4>
                        <p className="text-base">{orderDetails.deliveryWeek || '-'}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Tipo Pagamento</h4>
                        <p className="text-base">{orderDetails.paymentTypeLabel || '-'}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Data Creazione</h4>
                        <p className="text-base">{orderDetails.creationDate ? moment(orderDetails.creationDate).format('DD/MM/YYYY HH:mm') : '-'}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Totale Ordine</h4>
                        <p className="text-base font-bold text-green-700">€ {orderTotal.toFixed(2)}</p>
                    </div>
                </div>
            </div>

            {/* Articoli */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Articoli ({orderDetails.items?.length || 0})</h3>
                {orderDetails.items && orderDetails.items.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-gray-50">
                                    <th className="text-left p-3">Prodotto</th>
                                    <th className="text-left p-3">Cod. Fornitore</th>
                                    <th className="text-left p-3">Marca</th>
                                    <th className="text-right p-3">Qtà</th>
                                    <th className="text-right p-3">Prezzo</th>
                                    <th className="text-right p-3">Sconto</th>
                                    <th className="text-right p-3">Netto</th>
                                    <th className="text-center p-3">Consegnato</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderDetails.items.map((item, index) => (
                                    <tr key={index} className="border-b hover:bg-gray-50">
                                        <td className="p-3">{item.productLabel || '-'}</td>
                                        <td className="p-3">{item.productSupplierCode || '-'}</td>
                                        <td className="p-3">{item.productBrandLabel || '-'}</td>
                                        <td className="p-3 text-right">{item.quantityTotal || 0}</td>
                                        <td className="p-3 text-right">€ {(item.purchasePrice || 0).toFixed(2)}</td>
                                        <td className="p-3 text-right">{item.purchasePriceDiscount || 0}%</td>
                                        <td className="p-3 text-right font-medium">€ {(item.purchasePriceDiscounted || item.purchasePrice || 0).toFixed(2)}</td>
                                        <td className="p-3 text-center">
                                            {item.delivered ? (
                                                <span className="text-green-600"><DynamicIcon iconClass="fa-solid fa-check" /></span>
                                            ) : (
                                                <span className="text-gray-400"><DynamicIcon iconClass="fa-solid fa-minus" /></span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-gray-500 text-center py-4">Nessun articolo nell'ordine</p>
                )}
            </div>

            {/* Pagamenti */}
            {orderDetails.payments && orderDetails.payments.length > 0 && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-semibold mb-3">Pagamenti</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-gray-50">
                                    <th className="text-left p-3">Data</th>
                                    <th className="text-right p-3">Importo</th>
                                    <th className="text-left p-3">Stato</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderDetails.payments.map((payment, index) => (
                                    <tr key={index} className="border-b">
                                        <td className="p-3">{payment.date ? moment(payment.date).format('DD/MM/YYYY') : '-'}</td>
                                        <td className="p-3 text-right">€ {(payment.value || 0).toFixed(2)}</td>
                                        <td className="p-3">{payment.status || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="flex justify-end">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" />
                    Chiudi
                </button>
            </div>

            {/* Delete confirmation dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Conferma Eliminazione</h3>
                        <p className="mb-6">
                            Sei sicuro di voler eliminare l'ordine "{orderDetails.code}"?
                            Questa azione non può essere annullata.
                        </p>

                        {deleteError && (
                            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                                <p>{typeof deleteError === 'string' ? deleteError : 'Errore durante l\'eliminazione'}</p>
                            </div>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                className="secondary"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleteLoading}
                            >
                                Annulla
                            </button>
                            <button
                                className="danger"
                                onClick={confirmDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                        Eliminazione...
                                    </>
                                ) : (
                                    <>
                                        <DynamicIcon iconClass="fas fa-trash" />
                                        Elimina
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
